import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useThree, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei'
import { FilterClock, useCameraControls, X, Y, selectSetActiveOrientationObject, selectSetUpAndForward, selectAddParent, selectRemoveParent } from './CameraControls';
import { checkLimits } from '../../machines';
import { V1 } from '../../constants/machine-state/machine';
import { toolLength } from '../../machines/v1kickstarter';

export default function Model({ 
  joints = [ 0, 0, 0, 0, 0 ],
  tool = null,
  model = null,
  smoothJoints = 1,
  targetFPS = 120,
  modelOffsets = { X: 0, Y: 0, Z:0, A: 0, B:0, C: 0 },
  children,
  ...props 
}) {
  const group = useRef()
  const { nodes, materials } = useGLTF('./gltf/v1kickstarter.glb')

  const { scene } = useThree();

  const xRef = useRef();
  const yRef = useRef();
  const baseRef = useRef();
  const spindleRef = useRef();
  const tableRef = useRef();
  const trunnionRef = useRef();
  const partRef = useRef();

  const addParent = useCameraControls(selectAddParent);
  const setActiveOrientationObject = useCameraControls(selectSetActiveOrientationObject);
  const removeParent = useCameraControls(selectRemoveParent);
  const setUpAndForward = useCameraControls(selectSetUpAndForward);

  const jointColors = useMemo(() => joints.map((joint, jointIndex) => checkLimits(V1, jointIndex, joint) ? 0x000000 : 0xaa0000 ), [ joints ]);

  const [ clock ]  = useState(new FilterClock());

  useFrame(() => {
    const performFiltering = () => {
      if(xRef.current) {
        xRef.current.position.x = xRef.current.position.x*(1-smoothJoints) + smoothJoints*joints[0];
      }
      if(yRef.current) {
        yRef.current.position.y = yRef.current.position.y*(1-smoothJoints) + smoothJoints*(-joints[1]);
      }
      if(spindleRef.current) {
        spindleRef.current.position.z = spindleRef.current.position.z*(1-smoothJoints) + smoothJoints*toolLength(joints[2]);
      }

      if(trunnionRef.current) {
        trunnionRef.current.rotation.x = trunnionRef.current.rotation.x*(1-smoothJoints) + smoothJoints*Math.PI/180*joints[3];
      }

      if(tableRef.current) {
        tableRef.current.rotation.y= tableRef.current.rotation.y*(1-smoothJoints) + smoothJoints*Math.PI/180*joints[4];
      }
    }
    let deltaT = clock.getElapsedTime();
    const singleFrameTime = 1000/targetFPS;
    while(deltaT > singleFrameTime) {
      performFiltering();
      deltaT -= singleFrameTime;
      clock.offsetStart(singleFrameTime);
    }
  });

  useEffect(() => {
    const intensity = 3;
    const intensity2 = 1.25;
    materials.BaseMaterial.envMap = scene.environment;
    materials.BaseMaterial.envMapIntensity = intensity2;

    materials.StepperMaterial.envMap = scene.environment;
    materials.StepperMaterial.envMapIntensity = intensity;

    materials.ButtonRingMaterial.envMap = scene.environment;
    materials.ButtonRingMaterial.envMapIntensity = intensity;

    materials.RailsMaterial.envMap = scene.environment;
    materials.RailsMaterial.envMapIntensity = intensity2;

    materials.ButtonMaterial.envMap = scene.environment;
    materials.ButtonMaterial.envMapIntensity = intensity2;

    materials.XCarriageMaterial.envMap = scene.environment;
    materials.XCarriageMaterial.envMapIntensity = intensity;

    materials.ZRailMaterial.envMap = scene.environment;
    materials.ZRailMaterial.envMapIntensity = intensity2;

    materials.ToolHolderMaterial.envMap = scene.environment;
    materials.ToolHolderMaterial.envMapIntensity = intensity2;

    materials.SpindleMaterial.envMap = scene.environment;
    materials.SpindleMaterial.envMapIntensity = intensity;

    materials.ABackingPlateMaterial.envMap = scene.environment;
    materials.ABackingPlateMaterial.envMapIntensity = intensity;

    materials.TableMaterial.envMap = scene.environment;
    materials.TableMaterial.envMapIntensity = intensity2;

    materials.TrunnionMaterial.envMap = scene.environment;
    materials.TrunnionMaterial.envMapIntensity = intensity;

    const base = { name: "Base", object: baseRef.current };
    const spindle = { name: "Spindle", object: spindleRef.current };
    const table = { name: "Table", object: tableRef.current };
    const part = { name: "Part", object: partRef.current };
    const trunnion = { name: "Trunnion", object: trunnionRef.current };

    addParent(base);
    setActiveOrientationObject(base)

    addParent(spindle);
    addParent(table);
    addParent(trunnion);
    addParent(part);

    setUpAndForward(Y, X);

    return () => {
      removeParent(base);
      removeParent(spindle);
      removeParent(table);
      removeParent(trunnion);
      removeParent(part);
    };
  }, [ addParent, removeParent, setUpAndForward, materials, scene, setActiveOrientationObject ]);

  return (<group ref={group} {...props} dispose={null}>
      <group ref={baseRef}>
        <spotLight intensity={0.5} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[-27.51, 17.11, 13.31]} />
        <spotLight intensity={0.5} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[-22.38, -37.04, 13.82]} />
        <spotLight intensity={0.15} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[23.41, -31.99, 11.74]} />
        <spotLight intensity={0.15} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[45.3, 28.49, 13.89]} />
        <spotLight intensity={0.07} angle={Math.PI / 8} penumbra={0.15} decay={2} position={[49.23, 10.06, -26.13]} />
        <mesh geometry={nodes.V1_Body_1.geometry} material={materials.BaseMaterial} />
        <mesh geometry={nodes.V1_Body_2.geometry} material={materials.StepperMaterial} />
        <mesh geometry={nodes.Button_Ring.geometry} material={materials.ButtonRingMaterial} />
        <mesh geometry={nodes.Rails.geometry} material={materials.RailsMaterial} />
        <mesh geometry={nodes.V1_Yellow_Button.geometry} material={materials.ButtonMaterial} />
        <mesh geometry={nodes.x.geometry} material={materials.XCarriageMaterial} material-emissive={jointColors[0]} ref={xRef}>
          <mesh geometry={nodes.zrail.geometry} material={materials.ZRailMaterial}>
            <group ref={spindleRef}>
              { tool }
              <mesh geometry={nodes.z_1.geometry} material={materials.ToolHolderMaterial} material-emissive={jointColors[2]} />
              <mesh geometry={nodes.z_2.geometry} material={materials.SpindleMaterial} material-emissive={jointColors[2]} />
            </group>
          </mesh>
        </mesh>
        <mesh geometry={nodes.y.geometry} material={materials.ABackingPlateMaterial} material-emissive={jointColors[1]} ref={yRef}>
          <mesh geometry={nodes.trunnion.geometry} material={materials.TrunnionMaterial} ref={trunnionRef} material-emissive={jointColors[3]}>
            <mesh geometry={nodes.b.geometry} material={materials.TableMaterial} ref={tableRef} material-emissive={jointColors[4]}>
              <group position={[ modelOffsets.X, modelOffsets.Y, modelOffsets.Z ]} rotation-x={ modelOffsets.A*Math.PI/180 } rotation-y={ modelOffsets.B*Math.PI/180 }>
                <group ref={partRef}></group>
                {model}
              </group>
              {children}
            </mesh>
          </mesh>
        </mesh>
      </group>
    </group>
  )
}
